import type { ApiError, ApiErrorKind, AuthFailureKind, HttpMethod, ParseError } from '@visious/core';
/*
 * A full path with the extension, rather than `'../strings'`, and the same on the two hops behind it.
 * Vite resolves a directory import; **Node does not**, and this is the one file in `apps/web` that
 * `core-testkit` imports and *runs* (`problem-copy.test.ts`, zero dependencies, deliberately) — so it
 * has to obey the same rule `packages/core` does. `allowImportingTsExtensions` is already on for this
 * project, for exactly that reason. Everywhere else the house style stands.
 */
import { text } from '../strings/index.ts';

/**
 * **What can go wrong with a request, and which words answer it** — the types and the table together.
 *
 * The two halves used to live apart: `ApiProblem` was declared in `api/videos.ts` (the first endpoint
 * module written) and everything imported it from there, which read as a mistake for eleven modules and
 * was one for this file — the sentences imported the shape from a module whose own imports reach the
 * browser. 4a moved the shape here and left a re-export behind, so no call site changed and this file
 * is now a pure function of two plain objects: no DOM, no React, no fetch, testable from `core-testkit`
 * (`test/problem-copy.test.ts`).
 *
 * **4e moved the sentences out again, and this time on purpose**: they are in `strings/en/problem.ts`
 * with the rest of the catalogue, and this file is the *mapping* — which classified failure gets which
 * entry, with no `default` arm. That is the half worth testing and the half that cannot be translated
 * wrong. `text()` is read per call rather than imported once, so the table cannot freeze one language
 * into a module-level constant.
 */

/**
 * A failed request, in the four ways it can fail before a *classified* HTTP error is even reached.
 *
 * `transport` is core's taxonomy (R6). The other three are this layer's, and each exists because
 * collapsing it into another one produces a wrong sentence.
 */
export type ApiProblem =
  | { readonly kind: 'transport'; readonly error: ApiError }
  | { readonly kind: 'bad-url'; readonly path: string }
  | { readonly kind: 'malformed-json' }
  /**
   * **A `200` with zero bytes**, which is not the same failure as "that is not JSON" and has to be told
   * apart from it — `malformed-json`'s copy sends the reader off to check their network or their proxy,
   * and this is Invidious answering with nothing at all.
   *
   * The trap is not new (documented captions, `/api/v1/channels/{ucid}/notatab`, an empty playlist id, a
   * malformed clip id, `/api/v1/channels/{ucid}/thumbnail`), but **`/api/v1/videos/{id}` is a new
   * place** and the one that matters most, because it is the Watch page's only request. Found in a
   * browser during 3c: `jNQXAC9IVRw` — the first video ever uploaded to YouTube — answers `200`,
   * `application/json`, **0 bytes**, on 3 of 3 consecutive requests, while other videos answer normally.
   * So it is per-video and deterministic rather than a blip, and every fixture in this corpus that names
   * that id was captured back when it worked.
   *
   * Since 4a every endpoint module reaches it, through `api/json.ts` — before, three did and four
   * reported the same bytes as `malformed-json`.
   */
  | { readonly kind: 'empty-response' }
  | { readonly kind: 'parse'; readonly error: ParseError };

/**
 * A call the grant provably cannot make. Worth its own arm rather than firing the request and
 * reading the 403: `Invalid scope` and `Token is expired` are the same status, and deciding
 * client-side keeps the "re-authorize" path from being taken on a failure re-authorizing cannot fix.
 */
export interface ScopeProblem {
  readonly kind: 'out-of-scope';
  readonly method: HttpMethod;
  readonly endpoint: string;
}

export type AuthApiProblem = ApiProblem | ScopeProblem;

/**
 * R6's error kinds turned into something a reader can act on — once, rather than once per screen.
 *
 * This was written inside `screens/watch/model.ts` because Watch was the only page that made
 * requests. 2b is the second caller and 2c–2e are the next three, so it lifts on the same rule
 * `pickImage` and `channelRouteOf` lifted on. The taxonomy is shared; the *words* are not — a screen
 * passes in the two sentences that are genuinely about its subject, because "Video not found" is the
 * wrong sentence about a feed and "this feed does not exist here" is the wrong one about a video.
 */
export interface ProblemCopy {
  readonly title: string;
  readonly detail: string;
  /** Whether offering a retry is honest. A config flag and a bad payload do not clear on a retry. */
  readonly retryable: boolean;
}

export interface ProblemSubject {
  /** HTTP 404 — the one kind whose meaning is entirely about what was being fetched. */
  readonly notFound: ProblemCopy;
  /** A 200 whose body parsed as JSON but not as the thing it should be. */
  readonly unreadable: (error: ParseError) => ProblemCopy;
  /**
   * The instance's administrator switched this endpoint off (§8's "grey out with a reason"). Only
   * some endpoints can be — `popular_enabled: false` is a real Invidious setting — so a screen that
   * cannot meet it need not supply the sentence.
   */
  readonly disabled?: ProblemCopy;
  /**
   * Invidious answering with an error of its own rather than data. Optional because the generic
   * sentence below is honest everywhere — but a *channel* is the first subject where this failure has
   * a specific, reachable cause worth naming: an auto-generated "- Topic" channel answers this way
   * every time, and about one channel in thirty reachable from a plain search does.
   * "Invidious could not build this response" is true there and tells the reader nothing they can use.
   */
  readonly upstreamBroken?: ProblemCopy;
  /**
   * The subject was **taken down**, and the response said so (`videoGoneSubject`). Worth its own
   * sentence where the subject is a video: "the link may be wrong" is the wrong half of `notFound` to
   * offer about a link that was right and a video that is gone.
   */
  readonly removed?: ProblemCopy;
  /**
   * The subject exists and is **not shared**. A different fact from being removed and a different
   * thing for the reader to do about it, which is why the two are not one arm.
   */
  readonly private?: ProblemCopy;
  /**
   * The subject is a **live stream that has not started**, which is the one member of this group that
   * is not about something being wrong: the link is right, the video is fine, and it is not on yet.
   * A function because the wait travels with the failure: **seconds**, or `null` where the
   * instance's sentence carried no number. Seconds rather than a phrase, because this file is the
   * mapping and not the wording — the screen that supplies the sentence is the one holding a
   * formatter, and it is the same split `unreadable` already makes.
   */
  readonly notYetLive?: (startsInSec: number | null) => ProblemCopy;
  /**
   * The subject exists and YouTube will only hand it over to a signed-in account old enough for it.
   * Its own arm beside {@link removed} and {@link private} for their reason: the reader has done
   * nothing wrong, the link is right, and the way on is a destination this instance is not, which is
   * three facts none of the other two sentences carry.
   */
  readonly ageRestricted?: ProblemCopy;
  /**
   * The subject exists and YouTube shows it only to the channel's paying members, which the
   * instance's account is not. Beside {@link ageRestricted} for its reason: the link is right and
   * the video plays elsewhere — what stands in the way here is a membership, not an age check, and
   * that is a different thing for the reader to know.
   */
  readonly membersOnly?: ProblemCopy;
}

/**
 * The five meanings of one `403`, in words, and the whole reason `classifyAuthFailure`
 * exists is that only the body separates them.
 *
 * It lives here rather than on the sign-in page because **every** authenticated screen hits these, and
 * a browser showed what happens without it: a token the instance would not sign reported "this
 * endpoint needs a signed-in session on this instance" to a reader who *was* signed in. That sentence
 * is true for exactly one of the five and misleading for the other four — and `invalid-scope` is the
 * one where the obvious next step (refresh the grant) provably cannot work (`decideAuthRecovery`).
 */
function authCopy(kind: AuthFailureKind | undefined): ProblemCopy {
  const auth = text().problem.auth;
  switch (kind) {
    case 'not-authenticated':
      return auth.notAuthenticated;
    case 'token-expired':
      return auth.tokenExpired;
    case 'invalid-signature':
      return auth.invalidSignature;
    case 'invalid-scope':
      return auth.invalidScope;
    case 'malformed-token':
      return auth.malformedToken;
    default:
      return auth.unrecognised;
  }
}

/**
 * The same taxonomy for an authenticated call, which has one failure the others cannot have: a request
 * this app **refused to make** because the stored grant provably does not cover it (`ScopeProblem`).
 *
 * Worth its own arm rather than firing the request and reading the `403`, for `api/auth.ts`'s reason —
 * `Invalid scope` and `Token is expired` wear the same status, and only one of them is fixed by
 * re-authorizing. Lifted here on its second caller; the sign-in page wrote the first copy
 * and this one can be more specific, because the problem carries the method and the endpoint.
 */
export function describeAuthProblem(problem: AuthApiProblem, subject: ProblemSubject): ProblemCopy {
  if (problem.kind === 'out-of-scope') return text().problem.outOfScope;
  return describeProblem(problem, subject);
}

/**
 * **Every kind core can classify, with no `default` arm — which is the point of the table.**
 *
 * This used to be a `switch` with a fallback that printed the discriminant: four of the twelve kinds
 * had no case, so `urls-expired`, `server`, `client` and `malformed-response` reached a reader as
 * *"The instance returned an error — server (HTTP 500)."* A `Record<ApiErrorKind, …>` makes that
 * unspellable: adding a kind to core fails this build until somebody writes the sentence, which is the
 * same trick R1 plays with `no-restricted-globals` and R13 with dependency-cruiser. The taxonomy is
 * only worth having if every arm of it ends in words.
 *
 * `retryable` here is **not** `ApiError.retryable`, and the two disagree on purpose. That field is
 * §3's transport policy — "should the transport try this again by itself" — while this one answers "is
 * putting a Try again button in front of a person honest". `aborted` is the clearest case: the
 * transport has nothing to repeat, and a reader pressing the button is asking for the request the
 * cancellation took away.
 */
type KindCopy = (error: ApiError, subject: ProblemSubject) => ProblemCopy;

const COPY_BY_KIND: Readonly<Record<ApiErrorKind, KindCopy>> = {
  /*
   * The noun on the error picks the sentence where the response named one and the screen has words
   * for it. Every arm falls back to `notFound`, which is what a screen that cannot meet it gets
   * to keep saying — the same shape `disabled` and `upstreamBroken` have.
   */
  'video-unavailable': (error, subject) => {
    // The one arm about a video that is well: a scheduled stream that has not begun. It keeps the
    // `?? subject.notFound` fallback the others have, so a screen with no words for it says the
    // sentence it already had rather than nothing.
    if (error.notFound === 'video-not-yet-live') {
      return subject.notYetLive?.(error.startsInSec ?? null) ?? subject.notFound;
    }
    if (error.notFound === 'video-removed') return subject.removed ?? subject.notFound;
    if (error.notFound === 'video-private') return subject.private ?? subject.notFound;
    if (error.notFound === 'video-age-restricted') return subject.ageRestricted ?? subject.notFound;
    if (error.notFound === 'video-members-only') return subject.membersOnly ?? subject.notFound;
    return subject.notFound;
  },

  'endpoint-disabled': (_error, subject) => subject.disabled ?? text().problem.kind.endpointDisabled,

  'instance-warming': () => text().problem.kind.instanceWarming,

  'rate-limited': () => text().problem.kind.rateLimited,

  network: () => text().problem.kind.network,

  unauthorized: (error) => authCopy(error.auth),

  aborted: () => text().problem.kind.aborted,

  'upstream-broken': (_error, subject) => subject.upstreamBroken ?? text().problem.kind.upstreamBroken,

  /*
   * The kind is named for the case that matters most — a signed media URL past its ~5h45m — and this is
   * every *other* place the status lands. A page request fetches no media, so the expiry reading is
   * simply wrong here, and the two readings that are left are both refusals: something in front of
   * Invidious declined it, or Invidious declined it for a reason it did not put in the body (a body it
   * did put a reason in is `endpoint-disabled` or `unauthorized`, both matched before this).
   *
   * Not retryable: nothing about the request changes between two attempts, so a button would be a
   * promise the status cannot keep.
   */
  'urls-expired': () => text().problem.kind.urlsExpired,

  /*
   * A 5xx that survived §3's retries — the transport has already tried this three times with backoff by
   * the time a screen sees it, which is worth saying: it stops "just press it again" being the reader's
   * first thought, without taking the button away, because an instance that is restarting really does
   * come back.
   */
  server: () => text().problem.kind.server,

  /*
   * A 4xx that is none of 401, 403, 404 or 429 — so, in practice, this build sending something the
   * instance will not accept. Stated as ours rather than as the instance's fault, which is the same
   * reading `describeWriteProblem` takes of 3c's five malformed-body 403s.
   */
  client: () => text().problem.kind.client,

  /*
   * The transport's own reading of "a 200 whose body is not what the endpoint promised". `ApiProblem`
   * has a nearer arm for the shape this API actually produces — `empty-response`, checked per endpoint
   * before `JSON.parse` — so this one is reached only where a *caller* classified the body, and it
   * carries the same offer of one more attempt for the same measured reason.
   */
  'malformed-response': () => text().problem.kind.malformedResponse,
};

export function describeProblem(problem: ApiProblem, subject: ProblemSubject): ProblemCopy {
  switch (problem.kind) {
    case 'bad-url':
      /*
       * The path it could not build used to be in the sentence. It named an endpoint of an API the
       * reader does not call, while the one thing behind this failure they *own* — the instance address
       * in Settings — went unmentioned. So the copy points there instead; the path stays on the problem
       * for a log to pick up.
       */
      return text().problem.badUrl;

    case 'malformed-json':
      return text().problem.malformedJson;

    case 'empty-response':
      /*
       * A `200` with an empty body — Invidious itself answering with nothing, which this API does in at
       * least seven places. Its own arm because `malformed-json`'s sentence points at a proxy, and here
       * the instance really did reply: sending a reader to check their network about a response that
       * arrived is the wrong instruction.
       *
       * `retryable: true` even though the case 3c measured was deterministic (3 of 3 for one video). The
       * same shape is transient elsewhere in this API — `/vi/` images, `/companion/*` — so one retry is a
       * cheap way to find out which kind this is, and refusing to offer one would be a claim about the
       * cause that this arm cannot make.
       */
      return text().problem.emptyResponse;

    case 'parse':
      return subject.unreadable(problem.error);

    case 'transport':
      return COPY_BY_KIND[problem.error.kind](problem.error, subject);
  }
}
